import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * <FleetUpdateNotifier /> — quietly polls the deployed `index.html` for a
 * changed build hash and raises a "Fleet Update Available" toast prompting
 * the operator to refresh.
 *
 * Strategy: fetch `/` with cache-busting headers, hash the response, compare
 * to the snapshot taken on first mount. CRA emits new chunk filenames per
 * build, so the HTML payload changes whenever a new build lands. No service
 * worker required — works in every browser, including Director's iPhone PWA.
 *
 * Director directive 2026-02 · "Fleet Update Available" klaxon.
 */
const POLL_MS = 60_000; // every 60s — quiet enough to ignore, fast enough to catch a deploy
const STORAGE_KEY = "eigoo_build_hint";

async function fetchHash() {
    try {
        const res = await fetch(`/?_v=${Date.now()}`, {
            cache: "no-store",
            headers: { "Cache-Control": "no-cache" },
        });
        if (!res.ok) return null;
        const text = await res.text();
        // Match CRA's hashed JS bundle filename (main.<hash>.js or static/js/main.<hash>.chunk.js)
        const match = text.match(/static\/js\/main\.[a-f0-9]+\.(?:chunk\.)?js/i);
        if (match) return match[0];
        // Fallback: hash the full HTML length + a slice of body to catch any change
        return `len:${text.length}:${text.slice(-200).replace(/\s+/g, "")}`;
    } catch {
        return null;
    }
}

export default function FleetUpdateNotifier() {
    const baseline = useRef(null);
    const announced = useRef(false);

    useEffect(() => {
        let cancelled = false;
        let timer = null;

        const check = async () => {
            const hash = await fetchHash();
            if (cancelled || !hash) return;
            if (baseline.current == null) {
                baseline.current = hash;
                try { sessionStorage.setItem(STORAGE_KEY, hash); } catch { /* noop */ }
                return;
            }
            if (hash !== baseline.current && !announced.current) {
                announced.current = true;
                toast.message("Fleet Update Available", {
                    description: "A new build has landed. Refresh to sync the latest platform.",
                    duration: Infinity,
                    action: {
                        label: "Refresh",
                        onClick: () => window.location.reload(),
                    },
                    id: "fleet-update-toast",
                });
            }
        };

        // Establish baseline on mount, then poll.
        check();
        timer = setInterval(check, POLL_MS);

        // Re-check whenever the tab regains focus — catches the case where
        // the operator left the bridge for hours and a deploy landed quietly.
        const onFocus = () => check();
        window.addEventListener("focus", onFocus);

        return () => {
            cancelled = true;
            if (timer) clearInterval(timer);
            window.removeEventListener("focus", onFocus);
        };
    }, []);

    return null;
}
